import React, { useEffect, useState } from "react";
import CreditNotesFilters from "./CreditNotesFilters";
import CreditNotesTable from "./CreditNotesTable";
import { Box, Divider, Pagination, Typography } from "@mui/material";
import PageHeader from "../../../common/PageHeader";
import useIsMobile from "../../../common/mobileVersion/useIsMobile";
import {
  customerPaginationBoxStyle,
  paginationMobVer,
} from "../../ledger/ledgerStyles";
import { paginationCaptionStyle } from "../../customerManagement/DisplayCustomers.styles";
import { getAllCreditNotes } from "../../../api/generateCreditNoteAPI";
import moment from "moment";
import Toast from "../../../common/Toast";
import { exportToExcel } from "../../../utils/exportToExcel";

interface CreditNoteAPI {
  id: string;
  customer?: {
    outletName?: string;
  };
  customerId: string;
  grandTotal: string;
  createdAt: string;
  updatedAt: string;
  creditNoteUrl?: string | null;
  invoiceNos?: string[];
  orderIds?: string[];
}

const CreditManagementScreen = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<string>("Newest");
  const [creditNotes, setCreditNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalRows: 0,
    totalPages: 1,
  });

  const { page, rowsPerPage, totalPages, totalRows } = pagination;

  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);

  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "long",
      year: "2-digit",
    }).format(date);
  };

  const fetchCreditNotes = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getAllCreditNotes({
        page,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        startDate: from ? moment(from).format("YYYY-MM-DD") : null,
        endDate: to ? moment(to).format("YYYY-MM-DD") : null,
        sortOrder: sortBy === "Newest" ? "DESC" : "ASC",
      });

      const groups = response?.data?.groups || [];
      const notes: CreditNoteAPI[] = groups.flatMap(
        (g: any) => g?.creditNotes || []
      );
      const paginationData = response?.data?.pagination;

      setCreditNotes(
        notes.map((note: CreditNoteAPI) => ({
          customerName: note.customer?.outletName || "N/A",
          issuedDate: formatDate(note.createdAt),
          creditNoteId: note.id,
          amountCredited: note.grandTotal ? Number(note.grandTotal) : 0,
          invoiceNos: note.invoiceNos,
          orderIds: note.orderIds,
        }))
      );

      setPagination((prev) => ({
        ...prev,
        totalRows: paginationData?.totalRecords || 0,
        totalPages: paginationData?.totalPages || 1,
      }));
    } catch (err: any) {
      setToastMessage("Failed to load credit notes");
      setIsError(true);
      setShowToast(true);
      setError(err.message || "Failed to load credit notes");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, sortBy, from, to]);

  useEffect(() => {
    fetchCreditNotes();
  }, [fetchCreditNotes]);

  const handleChangePage = (_: any, newPage: number) => {
    if (!isMobile) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      const response = await getAllCreditNotes({
        page: 1,
        limit: 1000,
        search: searchTerm || undefined,
        startDate: from ? moment(from).format("YYYY-MM-DD") : null,
        endDate: to ? moment(to).format("YYYY-MM-DD") : null,
        sortOrder: sortBy === "Newest" ? "DESC" : "ASC",
      });

      const groups = response?.data?.groups || [];
      const notes: CreditNoteAPI[] = groups.flatMap(
        (g: any) => g?.creditNotes || []
      );

      if (notes.length === 0) {
        setToastMessage("No credit notes available to download");
        setIsError(true);
        setShowToast(true);
        return;
      }

      const excelData = notes.map((note: CreditNoteAPI) => ({
        "Customer Name": note.customer?.outletName || "N/A",
        "Issued Date": moment(note.createdAt).format("DD MMM YYYY"),
        "Invoice ID":
          note.invoiceNos && note.invoiceNos.length > 0
            ? note.invoiceNos.join(", ")
            : note.orderIds && note.orderIds.length > 0
              ? note.orderIds.join(", ")
              : "-",
        "Credit Note ID": note.id,
        "Amount Credited": note.grandTotal || "0.00",
      }));

      await exportToExcel(excelData, "credit-notes.xlsx");
    } catch (err) {
      setToastMessage("Failed to download credit notes");
      setIsError(true);
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader title="Credit Notes" />
      <Box display="flex" flexDirection="column">
        <CreditNotesFilters
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          sortBy={sortBy}
          setSortBy={setSortBy}
          from={from}
          to={to}
          setFrom={setFrom}
          setTo={setTo}
          handleExport={handleDownload}
          sortOptions={["Newest", "Oldest"]}
        />

        <Divider color="#EAECF0" />

        <Box padding="55px 114px" bgcolor="#FFF">
          {loading ? (
            <Typography align="center">Loading...</Typography>
          ) : error ? (
            <Typography align="center" color="error">
              {error}
            </Typography>
          ) : creditNotes.length > 0 ? (
            <CreditNotesTable data={creditNotes} />
          ) : (
            <Typography align="center">No credit notes found.</Typography>
          )}
        </Box>

        {/* Pagination */}
        {!isMobile && (
          <Box
            sx={{
              ...customerPaginationBoxStyle,
              bgcolor: "#fff",
            }}
          >
            <Typography variant="body2" sx={paginationCaptionStyle}>
              Showing data {startIndex + 1} to {endIndex} of {totalRows} entries
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              shape="rounded"
              variant="outlined"
              sx={paginationMobVer(isMobile)}
            />
          </Box>
        )}
      </Box>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={isError}
      />
    </>
  );
};

export default CreditManagementScreen;
